import { ComponentProps } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { collectFormErrors } from '../../../../utils/collect-form-errors';
import { Button } from '../../../atoms/button/button';
import { FormError } from '../../../atoms/form-error/form-error';
import { Header } from '../../../atoms/header/header';
import { Input } from '../../../atoms/input/input';
import { Label } from '../../../atoms/label/label';
import { Text } from '../../../atoms/text/text';
import { createSubmitHandler } from '../handle-submit';
import { useValidateEmailOrPhone } from '../username-validation';

type Inputs = {
  username: string;
};

const UsernameField = ({ variant, ...props }: ComponentProps<typeof Input>) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor="username">{t('auth.common.usernameLabel')}</Label>
      <Input type="text" variant={variant} {...props} placeholder={t('auth.common.usernamePlaceholder')} />
    </div>
  );
};

const FormContent = ({
  errors,
  register,
  t,
}: {
  readonly errors: Partial<Record<keyof Inputs, { message?: string }>>;
  readonly register: ReturnType<typeof useForm<Inputs>>['register'];
  readonly t: (key: string) => string;
}) => {
  const validate = useValidateEmailOrPhone();
  return (
    <div className="flex flex-col gap-6">
      <Header level="h1">{t('auth.forgotPassword.title')}</Header>
      <Text>{t('auth.forgotPassword.description')}</Text>
      <UsernameField
        variant={errors.username ? 'error' : 'default'}
        {...register('username', {
          required: t('auth.common.errors.usernameRequired'),
          validate,
        })}
      />
    </div>
  );
};

const FormActions = ({
  formErrors,
  isSubmitting,
  t,
}: {
  readonly formErrors: string[];
  readonly isSubmitting: boolean;
  readonly t: (key: string) => string;
}) => (
  <div className="flex flex-col gap-4 mt-6">
    <FormError error={formErrors.length > 0 ? formErrors : null} icon="alert-circle" />
    <Button className="w-full" loading={isSubmitting} type="submit">
      {t('common.buttons.continue')}
    </Button>
  </div>
);

const ForgotPasswordForm = ({ onSubmit }: { readonly onSubmit: SubmitHandler<Inputs> }) => {
  const { t } = useTranslation();
  const {
    handleSubmit,
    register,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<Inputs>();
  const submitHandler = handleSubmit(createSubmitHandler({ onSubmit, setError }));
  const formErrors = collectFormErrors(errors, t);

  return (
    <form onSubmit={submitHandler}>
      <FormContent errors={errors} register={register} t={t} />
      <FormActions formErrors={formErrors} isSubmitting={isSubmitting} t={t} />
    </form>
  );
};

export { ForgotPasswordForm };
